import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Send, Mail, Users, Check, AlertCircle, Copy, ExternalLink, Clock, Calendar, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { pollEmailJob } from '../../lib/emailJobs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkEmailPage = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGalleries, setSelectedGalleries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('published');
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentResults, setSentResults] = useState(null);
  const [mode, setMode] = useState('galleries');
  
  // Pre-order review email state
  const [preorders, setPreorders] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedPreorders, setSelectedPreorders] = useState([]);
  const [eventFilter, setEventFilter] = useState('all');
  const [preorderStatusFilter, setPreorderStatusFilter] = useState('photos_assigned');
  const [showPreorderConfirm, setShowPreorderConfirm] = useState(false);
  const [sendingPreorders, setSendingPreorders] = useState(false);
  const [preorderProgress, setPreorderProgress] = useState({ current: 0, total: 0 });
  const [previewPreorderId, setPreviewPreorderId] = useState(null);
  const [preorderPreview, setPreorderPreview] = useState(null);
  const [loadingPreorderPreview, setLoadingPreorderPreview] = useState(false);
  
  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [showScheduled, setShowScheduled] = useState(false);
  
  const [emailTemplate, setEmailTemplate] = useState({
    subject: 'Your Photos Are Ready! - {gallery_name}',
    message: ''
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchGalleries();
    fetchScheduledEmails();
  }, [statusFilter]);

  useEffect(() => {
    axios.get(`${API}/email-template`)
      .then(res => setEmailTemplate(res.data))
      .catch(() => {});
    axios.get(`${API}/preorder-events`)
      .then(res => setEvents(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === 'preorders' || mode === 'custom') {
      axios.get(`${API}/preorders`)
        .then(res => setPreorders(Array.isArray(res.data) ? res.data : res.data.preorders || []))
        .catch(() => toast.error('Failed to load pre-orders'));
    }
    if (mode === 'custom') { fetchCustomTemplates(); fetchScheduledEmails(); }
  }, [mode]);

  const preorderEmail = (po) => po.parent_email || po.client_email;
  const isPreorderEligible = (po) => Boolean(preorderEmail(po)) && (po.assigned_photos || []).length > 0;

  const filteredPreorders = preorders.filter(po => {
    if (eventFilter !== 'all' && po.event_id !== eventFilter) return false;
    if (preorderStatusFilter !== 'all' && po.status !== preorderStatusFilter) return false;
    return true;
  });

  const togglePreorder = (pid) => {
    setSelectedPreorders(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };

  const selectAllPreorders = () => {
    const eligible = filteredPreorders.filter(isPreorderEligible).map(p => p.id);
    setSelectedPreorders(prev => prev.length === eligible.length ? [] : eligible);
  };

  // Load email preview when the confirm dialog opens or the previewed pre-order changes
  useEffect(() => {
    if (showPreorderConfirm && selectedPreorders.length > 0) {
      const id = selectedPreorders.includes(previewPreorderId) ? previewPreorderId : selectedPreorders[0];
      if (id !== previewPreorderId) setPreviewPreorderId(id);
      setLoadingPreorderPreview(true);
      axios.post(`${API}/preorders/review-email-preview`, {
        preorder_id: id,
        base_url: window.location.origin
      })
        .then(res => setPreorderPreview(res.data))
        .catch(() => toast.error('Failed to load email preview'))
        .finally(() => setLoadingPreorderPreview(false));
    }
  }, [showPreorderConfirm, previewPreorderId]);

  const sendPreorderReviewEmails = async () => {
    setSendingPreorders(true);
    setPreorderProgress({ current: 0, total: selectedPreorders.length });
    try {
      // Server runs large batches as a background job - no request timeouts, safe to keep polling
      const res = await axios.post(`${API}/preorders/bulk-ready-for-review`, {
        preorder_ids: selectedPreorders,
        base_url: window.location.origin
      }, { timeout: 60000 });
      let agg;
      if (res.data.status === 'queued') {
        const job = await pollEmailJob(res.data.job_id, (j) => {
          setPreorderProgress({ current: j.processed, total: j.total });
        });
        agg = job;
      } else {
        agg = res.data;
      }
      toast.success(`Sent ${agg.sent.length} review email${agg.sent.length === 1 ? '' : 's'}!`);
      if (agg.failed.length > 0) toast.error(`${agg.failed.length} failed to send`);
      if (agg.skipped.length > 0) {
        const reasons = [...new Set(agg.skipped.map(s => s.reason))].join(', ');
        toast.warning(`${agg.skipped.length} skipped (${reasons})`);
      }
      setSelectedPreorders([]);
      setShowPreorderConfirm(false);
      const refreshed = await axios.get(`${API}/preorders`);
      setPreorders(Array.isArray(refreshed.data) ? refreshed.data : refreshed.data.preorders || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send review emails');
    } finally {
      setSendingPreorders(false);
      setPreorderProgress({ current: 0, total: 0 });
    }
  };


  // ===== Custom pre-order email (compose + templates) =====
  const DEFAULT_CUSTOM_SUBJECT = "Action Required: Complete {child_name}'s Photo Order by July 30th";
  const DEFAULT_CUSTOM_MESSAGE = `Hi {parent_name},

This is a friendly reminder that {child_name}'s photo order hasn't been approved yet.

Please complete your photo and background selections and approve the order by 11:59 PM on July 30th. Orders not approved by the deadline will miss the order deadline for delivery.

It only takes a few minutes — just click the button below to finish up!`;
  const [customSubject, setCustomSubject] = useState(DEFAULT_CUSTOM_SUBJECT);
  const [customMessage, setCustomMessage] = useState(DEFAULT_CUSTOM_MESSAGE);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [customStatusFilter, setCustomStatusFilter] = useState('not_approved');
  const [customPaidOnly, setCustomPaidOnly] = useState(false);
  const [selectedCustom, setSelectedCustom] = useState([]);
  const [showCustomConfirm, setShowCustomConfirm] = useState(false);
  const [sendingCustom, setSendingCustom] = useState(false);
  const [customProgress, setCustomProgress] = useState({ current: 0, total: 0 });
  const [customPreviewId, setCustomPreviewId] = useState(null);
  const [customPreview, setCustomPreview] = useState(null);
  const [loadingCustomPreview, setLoadingCustomPreview] = useState(false);
  const [customScheduleMode, setCustomScheduleMode] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');
  const [customSkipApproved, setCustomSkipApproved] = useState(true);

  const fetchCustomTemplates = () => {
    axios.get(`${API}/preorder-email-templates`)
      .then(res => setCustomTemplates(res.data || []))
      .catch(() => {});
  };

  const NOT_APPROVED_READY = ['ready_for_review', 'photos_assigned'];
  const APPROVED_LIKE = ['approved', 'fulfilled', 'completed', 'cancelled'];
  const customStatusMatch = (po) => {
    if (customStatusFilter === 'not_approved') return NOT_APPROVED_READY.includes(po.status);
    if (customStatusFilter === 'not_approved_all') return !APPROVED_LIKE.includes(po.status);
    if (customStatusFilter === 'all') return true;
    return po.status === customStatusFilter;
  };
  const filteredCustomPreorders = preorders.filter(po => {
    if (eventFilter !== 'all' && po.event_id !== eventFilter) return false;
    if (customPaidOnly && po.payment_status !== 'paid') return false;
    return customStatusMatch(po);
  });

  // Keep selection in sync with visible recipients (filters can't hide selected people)
  useEffect(() => {
    setSelectedCustom(prev => prev.filter(id => filteredCustomPreorders.some(p => p.id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customStatusFilter, customPaidOnly, eventFilter]);

  const toggleCustom = (pid) => {
    setSelectedCustom(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };
  const selectAllCustom = () => {
    const eligible = filteredCustomPreorders.filter(po => preorderEmail(po)).map(p => p.id);
    setSelectedCustom(prev => prev.length === eligible.length ? [] : eligible);
  };

  const loadTemplate = (tid) => {
    setSelectedTemplateId(tid);
    const t = customTemplates.find(x => x.id === tid);
    if (t) {
      setCustomSubject(t.subject);
      setCustomMessage(t.message);
      toast.success(`Loaded template "${t.name}"`);
    }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Template name (e.g. "Order Deadline Reminder"):');
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post(`${API}/preorder-email-templates`, {
        name: name.trim(), subject: customSubject, message: customMessage
      });
      setCustomTemplates(prev => [res.data, ...prev]);
      setSelectedTemplateId(res.data.id);
      toast.success(`Template "${name.trim()}" saved`);
    } catch {
      toast.error('Failed to save template');
    }
  };

  const updateTemplate = async () => {
    const t = customTemplates.find(x => x.id === selectedTemplateId);
    if (!t) return;
    try {
      await axios.put(`${API}/preorder-email-templates/${t.id}`, {
        name: t.name, subject: customSubject, message: customMessage
      });
      fetchCustomTemplates();
      toast.success(`Template "${t.name}" updated`);
    } catch {
      toast.error('Failed to update template');
    }
  };

  const deleteTemplate = async () => {
    const t = customTemplates.find(x => x.id === selectedTemplateId);
    if (!t || !window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/preorder-email-templates/${t.id}`);
      setCustomTemplates(prev => prev.filter(x => x.id !== t.id));
      setSelectedTemplateId('');
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  // Live preview inside the confirm dialog
  useEffect(() => {
    if (showCustomConfirm && selectedCustom.length > 0) {
      const id = selectedCustom.includes(customPreviewId) ? customPreviewId : selectedCustom[0];
      if (id !== customPreviewId) { setCustomPreviewId(id); return; }
      setLoadingCustomPreview(true);
      axios.post(`${API}/preorders/custom-email-preview`, {
        preorder_id: id, subject: customSubject, message: customMessage, base_url: window.location.origin
      })
        .then(res => setCustomPreview(res.data))
        .catch(() => toast.error('Failed to load email preview'))
        .finally(() => setLoadingCustomPreview(false));
    }
  }, [showCustomConfirm, customPreviewId]);

  const sendCustomEmails = async () => {
    const targetIds = selectedCustom.filter(id => filteredCustomPreorders.some(p => p.id === id));
    if (targetIds.length !== selectedCustom.length) setSelectedCustom(targetIds);
    if (targetIds.length === 0) { toast.error('No recipients selected'); return; }
    if (customScheduleMode) {
      if (!customDate) {
        toast.error('Please select a date for scheduling');
        return;
      }
      setSendingCustom(true);
      try {
        const scheduledDateTime = new Date(`${customDate}T${customTime}:00`);
        const res = await axios.post(`${API}/preorders/schedule-custom-email`, {
          preorder_ids: targetIds,
          subject: customSubject,
          message: customMessage,
          scheduled_for: scheduledDateTime.toISOString(),
          base_url: window.location.origin,
          skip_if_approved: customSkipApproved
        });
        toast.success(res.data.message || 'Emails scheduled!', { duration: 8000 });
        setSelectedCustom([]);
        setShowCustomConfirm(false);
        setCustomScheduleMode(false);
        fetchScheduledEmails();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to schedule emails');
      } finally {
        setSendingCustom(false);
      }
      return;
    }
    setSendingCustom(true);
    setCustomProgress({ current: 0, total: selectedCustom.length });
    try {
      const res = await axios.post(`${API}/preorders/bulk-custom-email`, {
        preorder_ids: targetIds,
        subject: customSubject,
        message: customMessage,
        base_url: window.location.origin
      }, { timeout: 60000 });
      let agg;
      if (res.data.status === 'queued') {
        agg = await pollEmailJob(res.data.job_id, (j) => setCustomProgress({ current: j.processed, total: j.total }));
      } else {
        agg = res.data;
      }
      toast.success(`Sent ${agg.sent.length} email${agg.sent.length === 1 ? '' : 's'}!`);
      if (agg.failed.length > 0) toast.error(`${agg.failed.length} failed to send`);
      if (agg.skipped.length > 0) toast.warning(`${agg.skipped.length} skipped (no email on file)`);
      setSelectedCustom([]);
      setShowCustomConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingCustom(false);
      setCustomProgress({ current: 0, total: 0 });
    }
  };

  useEffect(() => {
    if (showPreview && selectedGalleries.length > 0) {
      setLoadingPreview(true);
      axios.post(`${API}/email-template/preview`, {
        subject: emailTemplate.subject,
        message: emailTemplate.message,
        gallery_id: selectedGalleries[0]
      })
        .then(res => {
          setPreviewHtml(res.data.html);
          setPreviewSubject(res.data.subject);
        })
        .catch(() => toast.error('Failed to load email preview'))
        .finally(() => setLoadingPreview(false));
    }
  }, [showPreview]);

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/email-template`, emailTemplate);
      toast.success('Email template saved - it will be used for all gallery emails');
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const fetchGalleries = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/galleries?${params}`);
      setGalleries(response.data);
    } catch (error) {
      toast.error('Failed to load galleries');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledEmails = async () => {
    try {
      const response = await axios.get(`${API}/email/scheduled`);
      setScheduledEmails(response.data);
    } catch (error) {
      console.error('Failed to load scheduled emails:', error);
    }
  };

  const toggleGallery = (galleryId) => {
    setSelectedGalleries(prev => 
      prev.includes(galleryId) 
        ? prev.filter(id => id !== galleryId)
        : [...prev, galleryId]
    );
  };

  const selectAll = () => {
    const galleriesWithEmail = galleries.filter(g => g.client_email);
    if (selectedGalleries.length === galleriesWithEmail.length) {
      setSelectedGalleries([]);
    } else {
      setSelectedGalleries(galleriesWithEmail.map(g => g.id));
    }
  };

  const getGalleryLink = (accessCode) => {
    return `${window.location.origin}/gallery/${accessCode}`;
  };

  const handleSendEmails = async () => {
    if (selectedGalleries.length === 0) {
      toast.error('Please select at least one gallery');
      return;
    }

    // If scheduling mode is enabled
    if (scheduleMode) {
      if (!scheduledDate) {
        toast.error('Please select a date for scheduling');
        return;
      }
      
      setSending(true);
      try {
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
        
        const response = await axios.post(`${API}/email/schedule`, {
          gallery_ids: selectedGalleries,
          subject: emailTemplate.subject,
          message_template: emailTemplate.message,
          scheduled_for: scheduledDateTime.toISOString()
        });
        
        toast.success(response.data.message);
        setSelectedGalleries([]);
        setShowPreview(false);
        setScheduleMode(false);
        setScheduledDate('');
        fetchScheduledEmails();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to schedule emails');
      } finally {
        setSending(false);
      }
      return;
    }

    // Send immediately (in chunks to avoid request timeouts - server paces ~2 emails/sec)
    setSending(true);
    try {
      const agg = { sent: [], failed: [], skipped: [] };
      for (let i = 0; i < selectedGalleries.length; i += 20) {
        const chunk = selectedGalleries.slice(i, i + 20);
        const response = await axios.post(`${API}/email/send-bulk`, {
          gallery_ids: chunk,
          subject: emailTemplate.subject,
          message_template: emailTemplate.message
        }, { timeout: 120000 });
        agg.sent.push(...response.data.sent);
        agg.failed.push(...response.data.failed);
        agg.skipped.push(...response.data.skipped);
      }
      
      const results = {
        total: agg.sent.length + agg.failed.length + agg.skipped.length,
        sent: agg.sent.length,
        failed: agg.failed.length + agg.skipped.length,
        galleries: [
          ...agg.sent.map(g => ({ name: g.name, email: g.email, status: 'sent' })),
          ...agg.failed.map(g => ({ name: g.name, email: g.email, status: 'failed', reason: g.reason })),
          ...agg.skipped.map(g => ({ name: g.name, email: '', status: 'no_email' }))
        ]
      };
      
      setSentResults(results);
      toast.success(`Sent ${results.sent} emails successfully!`);
      setSelectedGalleries([]);
      setShowPreview(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const handleCancelScheduled = async (scheduledId) => {
    try {
      await axios.delete(`${API}/email/scheduled/${scheduledId}`);
      toast.success('Scheduled email cancelled');
      fetchScheduledEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel scheduled email');
    }
  };

  const formatScheduledDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-CA', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getMinDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  const copyAllLinks = () => {
    const selectedGalleryData = galleries.filter(g => selectedGalleries.includes(g.id));
    const links = selectedGalleryData.map(g => 
      `${g.client_name || 'Client'}: ${getGalleryLink(g.access_code)}`
    ).join('\n');
    
    navigator.clipboard.writeText(links);
    toast.success('Links copied to clipboard!');
  };

  const galleriesWithEmail = galleries.filter(g => g.client_email);
  const galleriesWithoutEmail = galleries.filter(g => !g.client_email);
  const pendingScheduled = scheduledEmails.filter(s => s.status === 'pending');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading text-stone-800">Bulk Email</h1>
          <p className="text-stone-600 mt-1">Send gallery links to multiple clients at once</p>
        </div>
        {mode === 'custom' && pendingScheduled.length > 0 && (
          <Button
            variant="outline"
            onClick={() => setShowScheduled(true)}
            data-testid="custom-view-scheduled-btn"
          >
            <Clock className="h-4 w-4 mr-2" />
            Scheduled ({pendingScheduled.length})
          </Button>
        )}
        {mode === 'galleries' && (
        <div className="flex gap-2">
          {pendingScheduled.length > 0 && (
            <Button 
              variant="outline"
              onClick={() => setShowScheduled(true)}
              data-testid="view-scheduled-btn"
            >
              <Clock className="h-4 w-4 mr-2" />
              Scheduled ({pendingScheduled.length})
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={copyAllLinks}
            disabled={selectedGalleries.length === 0}
            data-testid="copy-links-btn"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy Links
          </Button>
          <Button 
            className="btn-action"
            onClick={() => setShowPreview(true)}
            disabled={selectedGalleries.length === 0}
            data-testid="preview-email-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            Preview & Send ({selectedGalleries.length})
          </Button>
        </div>
        )}
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={mode === 'galleries' ? 'default' : 'outline'}
          onClick={() => setMode('galleries')}
          data-testid="mode-galleries-btn"
        >
          <Mail className="h-4 w-4 mr-2" />
          Gallery Emails
        </Button>
        <Button
          variant={mode === 'preorders' ? 'default' : 'outline'}
          onClick={() => setMode('preorders')}
          data-testid="mode-preorders-btn"
        >
          <Check className="h-4 w-4 mr-2" />
          Pre-Order Review Emails
        </Button>
        <Button
          variant={mode === 'custom' ? 'default' : 'outline'}
          onClick={() => setMode('custom')}
          data-testid="mode-custom-btn"
        >
          <Send className="h-4 w-4 mr-2" />
          Custom Pre-Order Email
        </Button>
      </div>

      {/* Scheduled Emails Alert */}
      {pendingScheduled.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-600" />
              <p className="text-sm text-blue-800">
                <strong>{pendingScheduled.length} scheduled email{pendingScheduled.length > 1 ? 's' : ''}</strong> pending. 
                Next: {formatScheduledDate(pendingScheduled[0]?.scheduled_for)} ({(pendingScheduled[0]?.galleries || pendingScheduled[0]?.preorders)?.length} recipients)
              </p>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowScheduled(true)}
                className="ml-auto text-blue-700 hover:text-blue-900"
              >
                View All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Template */}
      {mode === 'galleries' && (<>
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-stone-800">Email Template</CardTitle>
          <CardDescription>Customize the email that will be sent to your clients</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              value={emailTemplate.subject}
              onChange={(e) => setEmailTemplate({ ...emailTemplate, subject: e.target.value })}
              data-testid="email-subject-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={emailTemplate.message}
              onChange={(e) => setEmailTemplate({ ...emailTemplate, message: e.target.value })}
              data-testid="email-message-input"
              rows={10}
              className="font-mono text-sm"
            />
            <p className="text-xs text-stone-500">
              Available variables: {'{client_name}'}, {'{gallery_name}'}, {'{gallery_link}'}, {'{access_code}'}. 
              The "View Your Gallery" button and access code box are added automatically below your message.
            </p>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={saveTemplate} disabled={savingTemplate} data-testid="save-template-btn">
                {savingTemplate ? 'Saving...' : 'Save as Default Template'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gallery Selection */}
      <Card className="border-stone-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-xl text-stone-800">Select Galleries</CardTitle>
              <CardDescription>Choose which clients should receive the email</CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
                {selectedGalleries.length === galleriesWithEmail.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-stone-100 rounded animate-pulse" />
              ))}
            </div>
          ) : galleries.length > 0 ? (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {galleriesWithEmail.length > 0 && (
                  <>
                    <p className="text-sm font-medium text-stone-700 mb-2">
                      <Users className="h-4 w-4 inline mr-1" />
                      With Email ({galleriesWithEmail.length})
                    </p>
                    {galleriesWithEmail.map((gallery) => (
                      <div 
                        key={gallery.id}
                        className={`flex items-center gap-4 p-4 rounded-sm border transition-colors cursor-pointer ${
                          selectedGalleries.includes(gallery.id) 
                            ? 'bg-amber-50 border-amber-300' 
                            : 'bg-white border-stone-200 hover:border-stone-300'
                        }`}
                        onClick={() => toggleGallery(gallery.id)}
                        data-testid={`gallery-row-${gallery.id}`}
                      >
                        <Checkbox
                          checked={selectedGalleries.includes(gallery.id)}
                          onCheckedChange={() => toggleGallery(gallery.id)}
                          data-testid={`gallery-checkbox-${gallery.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-800 truncate">{gallery.name}</p>
                          <p className="text-sm text-stone-500 truncate">
                            {gallery.client_name} • {gallery.client_email}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={gallery.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}>
                            {gallery.status}
                          </Badge>
                          <code className="text-xs bg-stone-100 px-2 py-1 rounded font-mono">
                            {gallery.access_code}
                          </code>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                
                {galleriesWithoutEmail.length > 0 && (
                  <>
                    <p className="text-sm font-medium text-stone-500 mt-6 mb-2">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      Missing Email ({galleriesWithoutEmail.length})
                    </p>
                    {galleriesWithoutEmail.map((gallery) => (
                      <div 
                        key={gallery.id}
                        className="flex items-center gap-4 p-4 rounded-sm border border-stone-200 bg-stone-50 opacity-60"
                      >
                        <Checkbox disabled />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-600 truncate">{gallery.name}</p>
                          <p className="text-sm text-stone-400 truncate">
                            {gallery.client_name || 'No client info'} • No email
                          </p>
                        </div>
                        <Badge className="bg-stone-100 text-stone-500">No Email</Badge>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No galleries found</p>
              <p className="text-sm text-stone-400 mt-1">Create galleries with client emails to send bulk emails</p>
            </div>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Pre-Order Review Emails */}
      {mode === 'preorders' && (
      <Card className="border-stone-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="font-heading text-xl">Pre-Order Review Notifications</CardTitle>
              <CardDescription>
                Email parents their personal photo review link. Each selected pre-order is marked "Ready for Review".
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-[200px]" data-testid="preorder-event-filter">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map(ev => (
                    <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={preorderStatusFilter} onValueChange={setPreorderStatusFilter}>
                <SelectTrigger className="w-[190px]" data-testid="preorder-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="photos_assigned">Photos Assigned</SelectItem>
                  <SelectItem value="awaiting_photos">Awaiting Photos</SelectItem>
                  <SelectItem value="ready_for_review">Ready for Review</SelectItem>
                  <SelectItem value="all">All Statuses</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={selectAllPreorders} data-testid="select-all-preorders-btn">
                <Check className="h-4 w-4 mr-2" />
                Select All Eligible
              </Button>
              <Button
                onClick={() => setShowPreorderConfirm(true)}
                disabled={selectedPreorders.length === 0}
                className="bg-amber-700 hover:bg-amber-800"
                data-testid="send-review-emails-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Preview & Send ({selectedPreorders.length})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPreorders.length > 0 ? (
            <ScrollArea className="h-[450px] pr-4">
              <div className="space-y-2">
                {filteredPreorders.map((po) => {
                  const eligible = isPreorderEligible(po);
                  const photoCount = (po.assigned_photos || []).length;
                  return (
                    <div
                      key={po.id}
                      className={`flex items-center gap-4 p-4 rounded-sm border transition-colors ${
                        !eligible ? 'bg-stone-50 border-stone-200 opacity-60' :
                        selectedPreorders.includes(po.id)
                          ? 'bg-amber-50 border-amber-300 cursor-pointer'
                          : 'bg-white border-stone-200 hover:border-stone-300 cursor-pointer'
                      }`}
                      onClick={() => eligible && togglePreorder(po.id)}
                      data-testid={`preorder-row-${po.id}`}
                    >
                      <Checkbox
                        checked={selectedPreorders.includes(po.id)}
                        disabled={!eligible}
                        onCheckedChange={() => eligible && togglePreorder(po.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-800 truncate">
                          {po.child_name} {po.team_name ? <span className="text-stone-400 font-normal">({po.team_name})</span> : null}
                        </p>
                        <p className="text-sm text-stone-500 truncate">
                          {po.parent_name || 'Parent'} • {preorderEmail(po) || 'No email'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {photoCount} photo{photoCount === 1 ? '' : 's'}
                        </Badge>
                        <Badge className={
                          po.status === 'photos_assigned' ? 'bg-blue-100 text-blue-700' :
                          po.status === 'ready_for_review' ? 'bg-green-100 text-green-700' :
                          'bg-stone-100 text-stone-600'
                        }>
                          {(po.status || '').replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No pre-orders match these filters</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Custom Pre-Order Email */}
      {mode === 'custom' && (<>
      <Card className="border-stone-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="font-heading text-xl">Compose Email</CardTitle>
              <CardDescription>
                Write your own email to parents — e.g. deadline reminders. Each email includes a personal "Complete My Order" button with the parent's review link.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={selectedTemplateId} onValueChange={loadTemplate}>
                <SelectTrigger className="w-[220px]" data-testid="custom-template-select">
                  <SelectValue placeholder="Load a saved template..." />
                </SelectTrigger>
                <SelectContent>
                  {customTemplates.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-stone-400">No saved templates yet</div>
                  ) : customTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplateId && (
                <>
                  <Button variant="outline" size="sm" onClick={updateTemplate} data-testid="update-template-btn">
                    Update Template
                  </Button>
                  <Button variant="outline" size="sm" onClick={deleteTemplate} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="delete-template-btn">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={saveAsTemplate} data-testid="save-as-template-btn">
                Save as Template
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="custom-subject">Subject Line</Label>
            <Input
              id="custom-subject"
              value={customSubject}
              onChange={(e) => setCustomSubject(e.target.value)}
              data-testid="custom-subject-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="custom-message">Message</Label>
            <Textarea
              id="custom-message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={10}
              className="font-mono text-sm"
              data-testid="custom-message-input"
            />
            <p className="text-xs text-stone-500">
              Available variables: {'{parent_name}'}, {'{child_name}'}, {'{team_name}'}, {'{review_link}'}.
              The "Complete My Order" button and review link are added automatically below your message.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-stone-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="font-heading text-xl">Select Recipients</CardTitle>
              <CardDescription>
                "Not Approved" shows everyone whose photos are ready but hasn't approved their order yet.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-[200px]" data-testid="custom-event-filter">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map(ev => (
                    <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={customStatusFilter} onValueChange={setCustomStatusFilter}>
                <SelectTrigger className="w-[240px]" data-testid="custom-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_approved">Not Approved (ready to review)</SelectItem>
                  <SelectItem value="not_approved_all">All Not Approved (incl. awaiting photos)</SelectItem>
                  <SelectItem value="ready_for_review">Ready for Review</SelectItem>
                  <SelectItem value="photos_assigned">Photos Assigned</SelectItem>
                  <SelectItem value="awaiting_photos">Awaiting Photos</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="all">All Statuses</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 px-1">
                <Checkbox
                  id="custom-paid-only"
                  checked={customPaidOnly}
                  onCheckedChange={setCustomPaidOnly}
                  data-testid="custom-paid-only-toggle"
                />
                <Label htmlFor="custom-paid-only" className="text-sm cursor-pointer whitespace-nowrap">Paid only</Label>
              </div>
              <Button variant="outline" onClick={selectAllCustom} data-testid="custom-select-all-btn">
                <Check className="h-4 w-4 mr-2" />
                Select All
              </Button>
              <Button
                onClick={() => setShowCustomConfirm(true)}
                disabled={selectedCustom.length === 0}
                className="bg-amber-700 hover:bg-amber-800"
                data-testid="custom-preview-send-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Preview & Send ({selectedCustom.length})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCustomPreorders.length > 0 ? (
            <ScrollArea className="h-[450px] pr-4">
              <div className="space-y-2">
                {filteredCustomPreorders.map((po) => {
                  const eligible = Boolean(preorderEmail(po));
                  return (
                    <div
                      key={po.id}
                      className={`flex items-center gap-4 p-4 rounded-sm border transition-colors ${
                        !eligible ? 'bg-stone-50 border-stone-200 opacity-60' :
                        selectedCustom.includes(po.id)
                          ? 'bg-amber-50 border-amber-300 cursor-pointer'
                          : 'bg-white border-stone-200 hover:border-stone-300 cursor-pointer'
                      }`}
                      onClick={() => eligible && toggleCustom(po.id)}
                      data-testid={`custom-preorder-row-${po.id}`}
                    >
                      <Checkbox
                        checked={selectedCustom.includes(po.id)}
                        disabled={!eligible}
                        onCheckedChange={() => eligible && toggleCustom(po.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-800 truncate">
                          {po.child_name} {po.team_name ? <span className="text-stone-400 font-normal">({po.team_name})</span> : null}
                        </p>
                        <p className="text-sm text-stone-500 truncate">
                          {po.parent_name || 'Parent'} • {preorderEmail(po) || 'No email'}
                        </p>
                      </div>
                      <Badge className={
                        po.status === 'photos_assigned' ? 'bg-blue-100 text-blue-700' :
                        po.status === 'ready_for_review' ? 'bg-green-100 text-green-700' :
                        'bg-stone-100 text-stone-600'
                      }>
                        {(po.status || '').replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No pre-orders match these filters</p>
            </div>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Custom Email Send Confirm Dialog */}
      <Dialog open={showCustomConfirm} onOpenChange={(open) => { if (!sendingCustom) { setShowCustomConfirm(open); if (!open) { setCustomPreview(null); setCustomPreviewId(null); } } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="custom-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Preview & Send Custom Email</DialogTitle>
            <DialogDescription>
              {selectedCustom.length} parent{selectedCustom.length === 1 ? '' : 's'} will receive this email with their personal review link. Order statuses are not changed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {selectedCustom.length > 1 && (
              <div className="flex items-center gap-2">
                <Label className="text-sm text-stone-600 whitespace-nowrap">Preview email for:</Label>
                <Select value={customPreviewId || ''} onValueChange={setCustomPreviewId}>
                  <SelectTrigger className="w-full" data-testid="custom-preview-select">
                    <SelectValue placeholder="Choose a pre-order" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedCustom.map(pid => {
                      const po = preorders.find(p => p.id === pid);
                      return (
                        <SelectItem key={pid} value={pid}>
                          {po ? `${po.child_name} — ${preorderEmail(po)}` : pid}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-stone-50 rounded-sm p-3">
              <p className="text-xs font-medium text-stone-500 mb-1">To: <span className="text-stone-700" data-testid="custom-preview-recipient">{customPreview?.recipient || '...'}</span></p>
              <p className="text-xs font-medium text-stone-500">Subject: <span className="text-stone-700" data-testid="custom-preview-subject">{customPreview?.subject || '...'}</span></p>
            </div>
            <div className="bg-stone-50 rounded-sm p-2">
              {loadingCustomPreview ? (
                <p className="text-sm text-stone-500 p-4">Loading preview...</p>
              ) : customPreview ? (
                <iframe
                  title="Custom Email Preview"
                  srcDoc={customPreview.html}
                  className="w-full bg-white rounded border border-stone-200"
                  style={{ height: '380px' }}
                  data-testid="custom-email-preview-iframe"
                />
              ) : (
                <p className="text-sm text-stone-500 p-4">No preview available</p>
              )}
            </div>
            <p className="text-xs text-stone-500">
              Each email is personalized with the parent's name, child's name, and their unique review link.
            </p>

            {/* Schedule Option */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="custom-schedule-mode"
                  checked={customScheduleMode}
                  onCheckedChange={setCustomScheduleMode}
                  data-testid="custom-schedule-toggle"
                />
                <Label htmlFor="custom-schedule-mode" className="flex items-center gap-2 cursor-pointer">
                  <Clock className="h-4 w-4 text-blue-600" />
                  Schedule for later
                </Label>
              </div>
              {customScheduleMode && (
                <div className="ml-6 space-y-3">
                  <div className="flex gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="custom-schedule-date" className="text-xs text-stone-500">Date</Label>
                      <Input
                        id="custom-schedule-date"
                        type="date"
                        value={customDate}
                        onChange={(e) => setCustomDate(e.target.value)}
                        min={getMinDate()}
                        className="w-40"
                        data-testid="custom-schedule-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="custom-schedule-time" className="text-xs text-stone-500">Time</Label>
                      <Input
                        id="custom-schedule-time"
                        type="time"
                        value={customTime}
                        onChange={(e) => setCustomTime(e.target.value)}
                        className="w-32"
                        data-testid="custom-schedule-time"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="custom-skip-approved"
                      checked={customSkipApproved}
                      onCheckedChange={setCustomSkipApproved}
                      data-testid="custom-skip-approved-toggle"
                    />
                    <Label htmlFor="custom-skip-approved" className="text-sm text-stone-600 cursor-pointer">
                      Skip anyone who has approved their order by send time
                    </Label>
                  </div>
                  <p className="text-xs text-stone-400">
                    Times are in your local timezone. Manage or cancel from the "Scheduled" button at the top of this page.
                  </p>
                </div>
              )}
            </div>
          </div>

          {sendingCustom && !customScheduleMode && (
            <div className="space-y-2">
              <div className="w-full bg-stone-100 rounded-full h-2">
                <div className="bg-amber-600 h-2 rounded-full transition-all"
                  style={{ width: `${customProgress.total ? (customProgress.current / customProgress.total) * 100 : 0}%` }} />
              </div>
              <p className="text-sm text-stone-500 text-center">
                Sending {customProgress.current} / {customProgress.total}... emails are sent from the server, so it's safe to close this page
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCustomConfirm(false); setCustomScheduleMode(false); }} disabled={sendingCustom}>
              Cancel
            </Button>
            <Button onClick={sendCustomEmails} disabled={sendingCustom || (customScheduleMode && !customDate)}
              className={customScheduleMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-700 hover:bg-amber-800'} data-testid="confirm-send-custom-btn">
              {customScheduleMode ? <Calendar className="h-4 w-4 mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              {sendingCustom
                ? (customScheduleMode ? 'Scheduling...' : 'Sending...')
                : customScheduleMode
                  ? `Schedule ${selectedCustom.length} Email${selectedCustom.length === 1 ? '' : 's'}`
                  : `Send ${selectedCustom.length} Email${selectedCustom.length === 1 ? '' : 's'} Now`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-Order Send Confirm Dialog */}
      <Dialog open={showPreorderConfirm} onOpenChange={(open) => { if (!sendingPreorders) { setShowPreorderConfirm(open); if (!open) { setPreorderPreview(null); setPreviewPreorderId(null); } } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="preorder-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Review & Send Emails</DialogTitle>
            <DialogDescription>
              {selectedPreorders.length} parent{selectedPreorders.length === 1 ? '' : 's'} will receive an email with their personal "Review Your Photos" link
              (view photos, choose favourites for each product, and approve the order). Each pre-order will be marked "Ready for Review".
            </DialogDescription>
          </DialogHeader>

          {/* Email Preview */}
          <div className="space-y-3">
            {selectedPreorders.length > 1 && (
              <div className="flex items-center gap-2">
                <Label className="text-sm text-stone-600 whitespace-nowrap">Preview email for:</Label>
                <Select value={previewPreorderId || ''} onValueChange={setPreviewPreorderId}>
                  <SelectTrigger className="w-full" data-testid="preview-preorder-select">
                    <SelectValue placeholder="Choose a pre-order" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedPreorders.map(pid => {
                      const po = preorders.find(p => p.id === pid);
                      return (
                        <SelectItem key={pid} value={pid}>
                          {po ? `${po.child_name} — ${preorderEmail(po)}` : pid}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-stone-50 rounded-sm p-3">
              <p className="text-xs font-medium text-stone-500 mb-1">To: <span className="text-stone-700" data-testid="preorder-preview-recipient">{preorderPreview?.recipient || '...'}</span></p>
              <p className="text-xs font-medium text-stone-500">Subject: <span className="text-stone-700" data-testid="preorder-preview-subject">{preorderPreview?.subject || '...'}</span></p>
            </div>
            <div className="bg-stone-50 rounded-sm p-2">
              {loadingPreorderPreview ? (
                <p className="text-sm text-stone-500 p-4">Loading preview...</p>
              ) : preorderPreview ? (
                <iframe
                  title="Review Email Preview"
                  srcDoc={preorderPreview.html}
                  className="w-full bg-white rounded border border-stone-200"
                  style={{ height: '380px' }}
                  data-testid="preorder-email-preview-iframe"
                />
              ) : (
                <p className="text-sm text-stone-500 p-4">No preview available</p>
              )}
            </div>
            <p className="text-xs text-stone-500">
              Each email is personalized with the parent's name, child's name, and their unique review link.
            </p>
          </div>

          {sendingPreorders && (
            <div className="space-y-2">
              <div className="w-full bg-stone-100 rounded-full h-2">
                <div className="bg-amber-600 h-2 rounded-full transition-all"
                  style={{ width: `${preorderProgress.total ? (preorderProgress.current / preorderProgress.total) * 100 : 0}%` }} />
              </div>
              <p className="text-sm text-stone-500 text-center">
                Sending {preorderProgress.current} / {preorderProgress.total}... emails are sent from the server, so it's safe to close this page
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreorderConfirm(false)} disabled={sendingPreorders}>
              Cancel
            </Button>
            <Button onClick={sendPreorderReviewEmails} disabled={sendingPreorders}
              className="bg-amber-700 hover:bg-amber-800" data-testid="confirm-send-review-emails-btn">
              <Mail className="h-4 w-4 mr-2" />
              {sendingPreorders ? 'Sending...' : `Send ${selectedPreorders.length} Emails`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Preview Email</DialogTitle>
            <DialogDescription>
              Review how your email will look before sending to {selectedGalleries.length} recipients
            </DialogDescription>
          </DialogHeader>
          
          {selectedGalleries.length > 0 && (
            <div className="space-y-4">
              <div className="bg-stone-50 rounded-sm p-4">
                <p className="text-sm font-medium text-stone-600 mb-1">Subject:</p>
                <p className="text-stone-800" data-testid="preview-subject">{previewSubject || emailTemplate.subject}</p>
              </div>
              
              <div className="bg-stone-50 rounded-sm p-2">
                <p className="text-sm font-medium text-stone-600 mb-2 px-2 pt-2">Email Preview (exactly as clients will see it):</p>
                {loadingPreview ? (
                  <p className="text-sm text-stone-500 p-4">Loading preview...</p>
                ) : (
                  <iframe
                    title="Email Preview"
                    srcDoc={previewHtml}
                    className="w-full bg-white rounded border border-stone-200"
                    style={{ height: '450px' }}
                    data-testid="email-preview-iframe"
                  />
                )}
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This will send emails to {selectedGalleries.length} clients. 
                  Each email will be personalized with their specific gallery link and access code.
                </p>
              </div>

              {/* Schedule Option */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="schedule-mode"
                    checked={scheduleMode}
                    onCheckedChange={setScheduleMode}
                  />
                  <Label htmlFor="schedule-mode" className="flex items-center gap-2 cursor-pointer">
                    <Clock className="h-4 w-4 text-blue-600" />
                    Schedule for later
                  </Label>
                </div>
                
                {scheduleMode && (
                  <div className="flex gap-3 ml-6">
                    <div className="space-y-1">
                      <Label htmlFor="schedule-date" className="text-xs text-stone-500">Date</Label>
                      <Input
                        id="schedule-date"
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={getMinDate()}
                        className="w-40"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="schedule-time" className="text-xs text-stone-500">Time</Label>
                      <Input
                        id="schedule-time"
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="w-32"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPreview(false); setScheduleMode(false); }}>
              Cancel
            </Button>
            <Button 
              className={scheduleMode ? "bg-blue-600 hover:bg-blue-700" : "btn-action"}
              onClick={handleSendEmails}
              disabled={sending || (scheduleMode && !scheduledDate)}
              data-testid="send-emails-btn"
            >
              {sending ? (
                scheduleMode ? 'Scheduling...' : 'Sending...'
              ) : scheduleMode ? (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule {selectedGalleries.length} Emails
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send {selectedGalleries.length} Emails Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scheduled Emails Dialog */}
      <Dialog open={showScheduled} onOpenChange={setShowScheduled}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Scheduled Emails
            </DialogTitle>
            <DialogDescription>
              View and manage your scheduled email campaigns
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {scheduledEmails.filter(s => s.status === 'pending').length > 0 ? (
              scheduledEmails
                .filter(s => s.status === 'pending')
                .map((scheduled) => {
                  const isCustom = scheduled.type === 'preorder_custom';
                  const recipients = isCustom ? (scheduled.preorders || []) : (scheduled.galleries || []);
                  return (
                  <div key={scheduled.id} className="border rounded-lg p-4" data-testid={`scheduled-email-${scheduled.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-stone-800">
                            {recipients.length} recipients
                          </p>
                          <Badge className={isCustom ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                            {isCustom ? 'Custom Pre-Order' : 'Gallery'}
                          </Badge>
                        </div>
                        <p className="text-sm text-stone-500">
                          {formatScheduledDate(scheduled.scheduled_for)}
                        </p>
                        <p className="text-xs text-stone-400 truncate max-w-xs">
                          Subject: {scheduled.subject}
                        </p>
                        {isCustom && scheduled.skip_if_approved && (
                          <p className="text-xs text-stone-400">Skips anyone who approves before send time</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelScheduled(scheduled.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`cancel-scheduled-${scheduled.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {recipients.slice(0, 3).map((r, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {r.name || r.child_name}
                        </Badge>
                      ))}
                      {recipients.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{recipients.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                  );
                })
            ) : (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500">No scheduled emails</p>
                <p className="text-sm text-stone-400">Schedule emails from the preview dialog</p>
              </div>
            )}

            {scheduledEmails.filter(s => s.status !== 'pending').length > 0 && (
              <>
                <p className="text-sm font-medium text-stone-600 pt-2">Recent</p>
                {scheduledEmails
                  .filter(s => s.status !== 'pending')
                  .slice(-5)
                  .reverse()
                  .map((scheduled) => {
                    const isCustom = scheduled.type === 'preorder_custom';
                    const recipients = isCustom ? (scheduled.preorders || []) : (scheduled.galleries || []);
                    const res = scheduled.results;
                    return (
                      <div key={scheduled.id} className="border border-stone-100 bg-stone-50 rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm text-stone-700 truncate">{scheduled.subject}</p>
                            <p className="text-xs text-stone-400">
                              {recipients.length} recipients • {formatScheduledDate(scheduled.sent_at || scheduled.scheduled_for)}
                              {res && ` — ${res.sent?.length || 0} sent${res.skipped?.length ? `, ${res.skipped.length} skipped` : ''}${res.failed?.length ? `, ${res.failed.length} failed` : ''}`}
                            </p>
                          </div>
                          <Badge className={
                            scheduled.status === 'sent' ? 'bg-green-100 text-green-700' :
                            scheduled.status === 'cancelled' ? 'bg-stone-200 text-stone-600' :
                            'bg-red-100 text-red-700'
                          }>
                            {scheduled.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduled(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={!!sentResults} onOpenChange={() => setSentResults(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Check className="h-6 w-6 text-green-600" />
              Emails Sent!
            </DialogTitle>
            <DialogDescription>
              Successfully sent {sentResults?.sent} of {sentResults?.total} emails
            </DialogDescription>
          </DialogHeader>
          
          {sentResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-green-700">{sentResults.sent}</p>
                  <p className="text-sm text-green-600">Sent</p>
                </div>
                <div className="bg-stone-50 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-stone-500">{sentResults.failed}</p>
                  <p className="text-sm text-stone-500">Skipped (no email)</p>
                </div>
              </div>
              
              <ScrollArea className="h-48 border rounded-sm">
                <div className="p-2 space-y-1">
                  {sentResults.galleries.map((g, i) => (
                    <div key={i} className="flex items-center justify-between py-2 px-3 hover:bg-stone-50 rounded">
                      <div className="truncate flex-1">
                        <p className="text-sm font-medium truncate">{g.name}</p>
                        <p className="text-xs text-stone-500 truncate">{g.email || 'No email'}</p>
                      </div>
                      <Badge className={g.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}>
                        {g.status === 'sent' ? 'Sent' : 'Skipped'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setSentResults(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkEmailPage;
